// src/components/pages/HistoryPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { useToast } from "../ui/use-toast";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { format } from 'date-fns';
import { CalendarIcon, Filter, SlidersHorizontal, Calculator } from 'lucide-react';
import _ from 'lodash';

const HistoryPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    to: new Date()
  });
  const [selectedMetrics, setSelectedMetrics] = useState(['weight', 'body_fat']);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  // Fetch history data with filters
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        let query = supabase
          .from('nutrition_history')
          .select('*')
          .eq('user_id', user.id)
          .gte('created_at', dateRange.from.toISOString())
          .lte('created_at', dateRange.to.toISOString())
          .order(sortConfig.key, { ascending: sortConfig.direction === 'asc' });

        const { data, error } = await query;
        if (error) throw error;

        const formattedData = data.map(entry => ({
          ...entry,
          date: format(new Date(entry.created_at), 'MM/dd/yyyy'),
        }));

        setHistory(formattedData);
      } catch (error) {
        console.error('Error fetching history:', error);
        toast({
          title: "Error",
          description: "Failed to load history data.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [user.id, dateRange, sortConfig, toast]);

  // Metric options for selection
  const metricOptions = [
    { value: 'weight', label: 'Weight' },
    { value: 'body_fat', label: 'Body Fat %' },
    { value: 'bmr', label: 'BMR' },
    { value: 'tdee', label: 'TDEE' },
    { value: 'target_calories', label: 'Target Calories' },
    { value: 'protein_grams', label: 'Protein (g)' },
    { value: 'carbs_grams', label: 'Carbs (g)' },
    { value: 'fat_grams', label: 'Fat (g)' }
  ];

  const renderMultiMetricChart = () => {
    if (history.length === 0) return null;
  
    const chartData = [...history].sort((a, b) => 
      new Date(a.created_at) - new Date(b.created_at)
    );
  
    const metrics = {
      left: selectedMetrics.filter(m => m !== 'body_fat'),
      right: selectedMetrics.filter(m => m === 'body_fat')
    };
  
    const getAxisDomain = (metrics, data) => {
      if (metrics.length === 0) return [0, 0];
      
      const allValues = data.flatMap(entry => 
        metrics.map(metric => entry[metric])
      );
      const min = Math.floor(Math.min(...allValues)); // Floor for min
      const max = Math.ceil(Math.max(...allValues));  // Ceil for max
      
      // If min and max are very close, create a reasonable range around them
      if (Math.abs(max - min) < max * 0.01) {
        const midPoint = Math.round((min + max) / 2);
        const padding = Math.ceil(max * 0.1); // Round up padding
        return [midPoint - padding, midPoint + padding];
      }
      
      // Otherwise add 5% padding to the range
      const padding = Math.ceil((max - min) * 0.05);
      return [min - padding, max + padding];
    };
  
    const leftDomain = getAxisDomain(metrics.left, chartData);
    const rightDomain = getAxisDomain(metrics.right, chartData);
  
    // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    
    return (
      <div className="bg-background border rounded-lg p-3 shadow-lg">
        <p className="font-medium mb-2">{label}</p>
        {payload.map((item, index) => {
          const metric = metricOptions.find(m => m.value === item.dataKey);
          let value = item.value;
          let unit = '';
          let description = '';

          // Add units and descriptions based on metric type
          switch (item.dataKey) {
            case 'weight':
              unit = ' lbs';
              description = 'Total body weight';
              break;
            case 'body_fat':
              unit = '%';
              description = 'Body fat percentage';
              break;
            case 'bmr':
              unit = ' calories';
              description = 'Basal Metabolic Rate - Calories burned at rest';
              break;
            case 'tdee':
              unit = ' calories';
              description = 'Total Daily Energy Expenditure';
              break;
            case 'target_calories':
              unit = ' calories';
              description = 'Daily calorie target based on your goal';
              break;
            case 'protein_grams':
              unit = 'g';
              description = 'Daily protein target';
              break;
            case 'carbs_grams':
              unit = 'g';
              description = 'Daily carbohydrate target';
              break;
            case 'fat_grams':
              unit = 'g';
              description = 'Daily fat target';
              break;
          }

          return (
            <div key={index} className="mb-2" style={{ color: item.color }}>
              <p className="font-medium">{metric?.label}: {Math.round(value)}{unit}</p>
              <p className="text-xs text-muted-foreground">{description}</p>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="h-[400px] mt-4">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis 
            yAxisId="left" 
            domain={leftDomain}
            hide={metrics.left.length === 0}
            tickFormatter={(value) => Math.round(value)} // Round tick values
          />
          <YAxis 
            yAxisId="right" 
            orientation="right" 
            domain={rightDomain}
            hide={metrics.right.length === 0}
            tickFormatter={(value) => Math.round(value)} // Round tick values
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          {selectedMetrics.map((metric, index) => {
            const isPercentage = metric === 'body_fat';
            return (
              <Line
                key={metric}
                type="monotone"
                dataKey={metric}
                stroke={`hsl(${index * 60}, 70%, 50%)`}
                yAxisId={isPercentage ? "right" : "left"}
                name={metricOptions.find(m => m.value === metric)?.label}
                connectNulls
                dot={{ r: 4 }} // Slightly larger dots for better visibility
                activeDot={{ r: 6, strokeWidth: 2 }} // Enhanced active dot
              />
            );
          })}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

  const renderMacroDistributionChart = () => {
    if (history.length === 0) return null;

    const latestEntry = history[0];
    const macroData = [
      {
        name: 'Protein',
        grams: latestEntry.protein_grams,
        calories: latestEntry.protein_grams * 4
      },
      {
        name: 'Carbs',
        grams: latestEntry.carbs_grams,
        calories: latestEntry.carbs_grams * 4
      },
      {
        name: 'Fat',
        grams: latestEntry.fat_grams,
        calories: latestEntry.fat_grams * 9
      }
    ];

    return (
      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={macroData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis yAxisId="left" orientation="left" />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip />
            <Legend />
            <Bar yAxisId="left" dataKey="grams" fill="#8884d8" name="Grams" />
            <Bar yAxisId="right" dataKey="calories" fill="#82ca9d" name="Calories" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  if (loading) {
    return <div className="text-center p-4">Loading...</div>;
  }

  return (
    <div className="container mx-auto p-4 max-w-4xl space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Progress History</CardTitle>
              <CardDescription>Track your nutrition and fitness progress over time</CardDescription>
            </div>
            <div className="flex gap-2">
              {/* Calculator Link */}
              <Button 
                onClick={() => navigate('/calculator')}
                variant="outline"
                className="flex gap-2"
              >
                <Calculator className="h-4 w-4" />
                Add New Entry
              </Button>

              {/* Date Range Selector */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="flex gap-2">
                    <CalendarIcon className="h-4 w-4" />
                    {format(dateRange.from, 'MMM d, yyyy')} - {format(dateRange.to, 'MMM d, yyyy')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={dateRange.from}
                    selected={{ from: dateRange.from, to: dateRange.to }}
                    onSelect={(range) => {
                      if (range?.from && range?.to) {
                        setDateRange({ from: range.from, to: range.to });
                      }
                    }}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>

              {/* Metric Selector */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="flex gap-2">
                    <SlidersHorizontal className="h-4 w-4" />
                    Metrics
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80">
                  <div className="space-y-2">
                    {metricOptions.map((metric) => (
                      <div key={metric.value} className="flex items-center">
                        <input
                          type="checkbox"
                          id={metric.value}
                          checked={selectedMetrics.includes(metric.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedMetrics([...selectedMetrics, metric.value]);
                            } else {
                              setSelectedMetrics(selectedMetrics.filter(m => m !== metric.value));
                            }
                          }}
                          className="mr-2"
                        />
                        <label htmlFor={metric.value}>{metric.label}</label>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No history data available for the selected period.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Multi-metric Progress Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Progress Over Time</CardTitle>
                </CardHeader>
                <CardContent>
                  {renderMultiMetricChart()}
                </CardContent>
              </Card>

              {/* Latest Macro Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Latest Macro Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  {renderMacroDistributionChart()}
                </CardContent>
              </Card>

              {/* Detailed History Log */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg">History Log</CardTitle>
                    <Select
                      value={`${sortConfig.key}-${sortConfig.direction}`}
                      onValueChange={(value) => {
                        const [key, direction] = value.split('-');
                        setSortConfig({ key, direction });
                      }}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Sort by..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="created_at-desc">Newest First</SelectItem>
                        <SelectItem value="created_at-asc">Oldest First</SelectItem>
                        <SelectItem value="weight-desc">Weight (High to Low)</SelectItem>
                        <SelectItem value="weight-asc">Weight (Low to High)</SelectItem>
                        <SelectItem value="body_fat-desc">Body Fat % (High to Low)</SelectItem>
                        <SelectItem value="body_fat-asc">Body Fat % (Low to High)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {history.map((entry) => (
                      <Card key={entry.id}>
                        <CardContent className="p-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <p className="text-sm text-muted-foreground">Date</p>
                              <p>{entry.date}</p>
                            </div>
                            <div>
                              <p className="text-sm text-muted-foreground">Weight</p>
                              <p>{entry.weight} lbs</p>
                            </div>
                            <div>
                              <p className="text-sm text-muted-foreground">Body Fat</p>
                              <p>{entry.body_fat}%</p>
                            </div>
                            <div>
                              <p className="text-sm text-muted-foreground">TDEE</p>
                              <p>{entry.tdee} cal</p>
                            </div>
                          </div>
                          {entry.notes && (
                            <div className="mt-2 pt-2 border-t">
                              <p className="text-sm text-muted-foreground">Notes</p>
                              <p>{entry.notes}</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HistoryPage;